import { useEffect } from "react";
import { motion } from "framer-motion";

/**
 * Cinematic Triarch entry loader.
 * Shown only on initial site entry (managed in App). Dark, institutional,
 * restrained: the logo masks in from black with a blur-to-sharp + subtle
 * scale, a faint muted-gold glow pulses behind it, a thin divider draws in,
 * and the "TRIARCH" mark settles before the whole layer fades into the
 * landing page.
 */
export const Loader = ({ onDone }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => onDone && onDone(), 2000);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [onDone]);

  const ease = [0.22, 1, 0.36, 1];

  return (
    <motion.div
      data-testid="triarch-loader"
      className="fixed inset-0 z-[300] flex flex-col items-center justify-center bg-[#050608]"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, filter: "blur(6px)", transition: { duration: 0.85, ease } }}
    >
      {/* faint grain texture */}
      <div className="triarch-noise pointer-events-none absolute inset-0 opacity-[0.06]" />

      {/* logo + glow halo (glow anchored to the logo, not the viewport) */}
      <div className="relative flex items-center justify-center">
        {/* muted gold / silver glow pulse behind the logo.
            Centering is on a static wrapper so Framer Motion's scale
            transform does not override the translate. */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2">
          <motion.div
            aria-hidden="true"
            className="h-full w-full rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(203,174,120,0.18), rgba(165,172,184,0.05) 34%, transparent 58%)",
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: [0.12, 0.4, 0.2], scale: [0.95, 1.04, 0.99] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        {/* logo: blur-to-sharp + slight scale + fade in (mask reveal feel) */}
        <motion.img
          src="/triarch-logo-gold.png"
          alt="Triarch"
          data-testid="triarch-loader-logo"
          className="relative h-28 w-28 sm:h-32 sm:w-32"
          initial={{ opacity: 0, scale: 0.96, filter: "blur(16px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.35, ease }}
        />
      </div>

      {/* thin divider line reveal */}
      <motion.div
        aria-hidden="true"
        className="relative mt-8 h-px w-24 bg-gradient-to-r from-transparent via-[#C9AE78]/55 to-transparent"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ duration: 0.95, delay: 0.55, ease }}
      />

      {/* mark */}
      <motion.span
        className="relative mt-5 inline-block font-display text-xs font-medium uppercase tracking-[0.55em] text-white/55"
        style={{ paddingLeft: "0.55em" }}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.85, ease }}
      >
        Triarch
      </motion.span>
    </motion.div>
  );
};
